import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Drawer, { FooterSpacer } from '../Drawer';
import Button from '../Button';
import StudentDetailsStep from './add/StudentDetailsStep';
import FamilyStep from './add/FamilyStep';
import LessonSettingsStep from './add/LessonSettingsStep';
import ConfirmationStep from './add/ConfirmationStep';
import useAddStudentForm, { STEPS, CONFIRMATION_STEP } from '../../hooks/useAddStudentForm';
import { useFamilies } from '../../data/useFamilies';
import { useSettings } from '../../data/useSettings';
import { familyDisplayName } from '../../domain/people';
import { familyPickerOrder } from '../../domain/familyAccount';
import s from './add/addStudent.module.css';

// Add New Student — the four-step wizard (SPEC.md §1.2).
//
// Assembly only: the form and the save are useAddStudentForm, each step renders
// itself, and this decides which one is on screen and what the footer says.

export default function AddStudentDrawer({ open, onClose }) {
  const navigate = useNavigate();
  const { families, loading: familiesLoading } = useFamilies();
  const { settings } = useSettings();
  const w = useAddStudentForm({ onClosed: onClose });

  // From the families collection, not inferred from students, so a family
  // with no students can be picked and the label is the family's own name.
  //
  // By name, in the core's `labelOrder` — the order Family Accounts, Add
  // Transaction and New Invoice list families in, and Android's Add Student.
  const familyOptions = useMemo(() => {
    const labels = families.map((f) => familyDisplayName(f));
    return familyPickerOrder(labels).map((i) => ({ id: families[i].id, label: labels[i] }));
  }, [families]);

  const footer = w.step === CONFIRMATION_STEP ? (
    <>
      <Button variant="secondary" onClick={w.addAnother}>Add Another Student</Button>
      <FooterSpacer />
      <Button onClick={w.close}>Done</Button>
    </>
  ) : (
    <>
      <Button variant="secondary" onClick={w.close}>Cancel</Button>
      <FooterSpacer />
      {w.step > 0 ? (
        <Button variant="secondary" onClick={() => w.setStep((s) => s - 1)}>Previous</Button>
      ) : null}
      {w.step < 2 ? (
        <Button onClick={() => w.setStep((s) => s + 1)} disabled={!w.canAdvance}>Next</Button>
      ) : (
        <>
          {/* Save refuses while the studio's tags are still being read, because
              the memberships would be dropped and the wizard would confirm an
              untagged student (#138). Said rather than a greyed button: a button
              is never greyed for a reason the teacher cannot act on
              (EditDrawer), and the window is a tick long. */}
          {w.tagsPending ? (
            <span className={s.footerNote}>Still reading the studio’s tags…</span>
          ) : null}
          <Button onClick={w.save} loading={w.saving}>Save</Button>
        </>
      )}
    </>
  );

  return (
    <Drawer
      open={open}
      onClose={w.close}
      title="Add New Student"
      steps={STEPS}
      currentStep={w.step}
      footer={footer}
    >
      {w.step === 0 ? (
        <StudentDetailsStep
          form={w.form}
          set={w.set}
          showMore={w.showMore}
          onToggleMore={() => w.setShowMore((v) => !v)}
          // The two tag pickers under Additional Details (#138), as one bag:
          // this drawer is assembly only.
          tagPicker={{
            tags: w.tags,
            tagsOfKind: w.tagsOfKind,
            tagsLoaded: w.tagsLoaded,
            selectedTagIds: w.selectedTagIds,
            onToggleTag: w.toggleTag,
            onCreateTag: w.createTag,
          }}
        />
      ) : null}

      {w.step === 1 ? (
        <FamilyStep
          form={w.form}
          set={w.set}
          families={familyOptions}
          familiesRead={!familiesLoading}
          // Whether to ask this family for a zone of its own — MMS's "Enable
          // Multi-Time Zone Selection", off unless the studio has remote
          // students. See EventSchedulingPanel.jsx.
          multiTimeZone={settings.scheduling.multiTimeZone}
          emailError={w.parentEmailError}
          asksForParent={w.asksForParent}
          adult={w.adult}
        />
      ) : null}

      {w.step === 2 ? (
        <LessonSettingsStep
          form={w.form}
          set={w.set}
          categories={w.categories}
          onCategoryChange={w.chooseCategory}
          rateError={w.rateError}
        />
      ) : null}

      {w.step === CONFIRMATION_STEP ? (
        <ConfirmationStep
          onScheduleLesson={() => { w.close(); navigate('/calendar'); }}
          onViewProfile={() => { w.close(); navigate(`/students/${w.created?.studentId}`); }}
        />
      ) : null}
    </Drawer>
  );
}
